import { Divider } from "components/design/Divider";
import { Button } from "components/DesignSystem/Button/Button";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import Modal from "components/DesignSystem/Modal/Modal";
import { FormikForm } from "components/FormikForm/FormikForm";
import { SUPPORT_EMAIL } from "constants/base";
import { FormikValues } from "formik";
import { useToast } from "hooks/useToast";
import { ReactNode, useState } from "react";
import { useUpdateBookkeepingSettingsMutation } from "store/apis/booksLockDate";
import { BackendError } from "types/utils/error";
import { boolean, object } from "yup";

const Step = ({
  number,
  children,
  isLast = false,
}: {
  number: number;
  children: ReactNode;
  isLast?: boolean;
}) => (
  <div className="t-flex t-gap-3 ">
    <div className="t-flex t-flex-col t-items-center">
      <div className="t-text-text-30 t-bg-neutral-0 t-rounded-full t-text-caption t-text-center t-h-4 t-w-4 t-py-[1px] t-px-1">
        {number}
      </div>
      {!isLast && (
        <div className="t-w-0.5 t-h-full t-bg-neutral-0 t-my-0.5 t-min-h-4" />
      )}
    </div>
    <div className="t-text-text-60 t-text-body-sm [&>b]:t-text-subtext t-self-start -t-mt-1">
      {children}
      <div className="t-invisible">
        This is a placeholder for additional content
      </div>
    </div>
  </div>
);

export const GustoAccess = ({ entityId }: { entityId: string }) => {
  const { alertToast, info: infoToast } = useToast();

  const [updateBookkeepingSettings, updateState] =
    useUpdateBookkeepingSettingsMutation();

  const handleConnectGusto = async (values: FormikValues) => {
    try {
      await updateBookkeepingSettings({
        entityId,
        gusto_access: values.agree,
      }).unwrap();

      infoToast({
        title: "Gusto access shared",
        message:
          "Our team will access your Gusto account and begin processing your payroll reports.",
      });
    } catch (error) {
      alertToast(
        {
          message: (error as BackendError).data?.error?.message,
        },
        error as Error
      );
    }
  };

  return (
    <Modal.Content asChild size="large">
      <FormikForm
        onSubmit={handleConnectGusto}
        initialValues={{ agree: false }}
        validationSchema={() =>
          object().shape({
            agree: boolean().oneOf(
              [true],
              "Please confirm you have completed the steps to connect your Gusto account"
            ),
          })
        }
        validateOnChange
        validateOnMount
      >
        {({ setFieldValue, isValid }) => (
          <>
            <Modal.Header>
              <Modal.Title>Share Gusto access</Modal.Title>
              <Modal.Close />
            </Modal.Header>
            <Modal.Body className="t-flex t-flex-col t-gap-6">
              <div className="t-flex t-flex-col t-gap-2">
                <div className="t-text-text-30 t-text-body-sm">
                  Follow these simple steps to connect with Gusto
                </div>
              </div>
              <div className="t-flex t-flex-col t-gap-2">
                <Step number={1}>
                  Log in as the primary administrator - Only the account owner
                  can add new users
                </Step>
                <Step number={2}>
                  Go to <b> Settings </b> from your Gusto dashboard
                </Step>
                <Step number={3}>
                  Click on Permissions tab. Select <b> "Add or edit admins" </b>{" "}
                  in the Gusto Admins section
                </Step>
                <Step number={4}>
                  Click <b> "Add new admin" </b>. Set employment status as{" "}
                  <b> "Not an employee" </b>
                </Step>
                <Step number={5}>
                  Choose
                  <b> "Accountant" </b>
                  from the <b>"Who are they?"</b> dropdown
                </Step>
                <Step number={6}>
                  Select <b> "Accounting/Bookkeeping" </b> under{" "}
                  <b> "What will they help you with?" </b>
                </Step>
                <Step number={7}>
                  <>
                    Enter our details:
                    <div className="t-ml-4 t-flex t-flex-col t-gap-1 t-text-sm t-text-text-50">
                      <div>1. First name: Inkle, Last name: Support</div>
                      <div>
                        2. Email address:{" "}
                        <span className="t-text-text-30 t-underline">
                          {SUPPORT_EMAIL}
                        </span>
                      </div>
                    </div>
                  </>
                </Step>
                <Step number={8}>
                  Select access level - Choose
                  <b> "Full Access" </b>
                  or customize permissions to include payroll and reports access
                </Step>

                <Step number={9} isLast={true}>
                  Click <b> "Add Admin" </b> to send the invitation
                </Step>
              </div>
              <Divider />
              <Checkbox
                onChange={(e) => setFieldValue("agree", e.target.checked)}
                name="agree"
                label="I've completed the steps to connect my Gusto account"
              />
            </Modal.Body>
            <Modal.FooterButtonGroup>
              <Modal.RawClose asChild>
                <Button disabled={updateState.isLoading}>Cancel</Button>
              </Modal.RawClose>
              <Button
                customType="primary"
                isLoading={updateState.isLoading}
                disabled={!isValid || updateState.isLoading}
                type="submit"
              >
                Done
              </Button>
            </Modal.FooterButtonGroup>
          </>
        )}
      </FormikForm>
    </Modal.Content>
  );
};
